import type { SafeAny } from '@/common/types';
import { type AxiosResponse } from 'axios';
import { axiosInstance, EPOCH_SIZES } from '@/common/utils';

const inFlightEpochSizeRequests = new Map<string, Promise<SafeAny>>();
const preloadedStudies = new Set<string>();

const normalizeFlag = (value: string) => String(value).toLowerCase();
const BASE_EPOCH_SIZE = Math.min(...EPOCH_SIZES.map((size) => size.inSeconds));
export const normalizeEpochSize = (epochSize: number) => (epochSize === -1 ? -1 : BASE_EPOCH_SIZE);
const buildRequestKey = (id: string, epochSize: number, notch: string, band: string) =>
  `${id}|${normalizeEpochSize(epochSize)}|${normalizeFlag(notch)}|${normalizeFlag(band)}`;
const buildConfigKey = (id: string, notch: string, band: string) =>
  `${id}|${normalizeFlag(notch)}|${normalizeFlag(band)}`;
const buildPreloadKey = (id: string, notch: string, band: string) =>
  `${id}|${normalizeFlag(notch)}|${normalizeFlag(band)}`;

const buildDerivedEpochSet = (baseCount: number, epochSize: number) => {
  if (epochSize === BASE_EPOCH_SIZE) {
    return Array.from({ length: baseCount }, () => ({}));
  }
  if (epochSize === -1 || baseCount <= 0) return [];
  const ratio = epochSize / BASE_EPOCH_SIZE;
  if (!Number.isInteger(ratio) || ratio <= 0) return [];
  const derivedCount = Math.ceil(baseCount / ratio);
  return Array.from({ length: derivedCount }, (_, index) => {
    const baseStart = index * ratio;
    const baseEnd = Math.min(baseStart + ratio - 1, baseCount - 1);
    return { baseEpochStart: baseStart, baseEpochEnd: baseEnd };
  });
};

const ensureConfigCache = (rootState: SafeAny) => {
  if (!rootState.study.config.cacheByKey) {
    rootState.study.config.cacheByKey = {};
  }
  return rootState.study.config.cacheByKey as Record<string, SafeAny>;
};

export const getCachedEpochConfig = (
  rootState: SafeAny,
  id: string,
  epochSize: number,
  notch: string,
  band: string
): SafeAny | undefined => {
  const cache = ensureConfigCache(rootState);
  const key = buildConfigKey(id, notch, band);
  const cached = cache[key];
  if (!cached?.sets?.[epochSize]) return undefined;
  return cached;
};

const storeConfigCache = (
  rootState: SafeAny,
  id: string,
  epochSize: number,
  notch: string,
  band: string,
  data: { data: SafeAny[]; config: SafeAny }
): SafeAny => {
  const cache = ensureConfigCache(rootState);
  const key = buildConfigKey(id, notch, band);
  const existing = cache[key] || { config: data.config, sets: {} };
  const sets = {
    ...existing.sets
  } as Record<string, SafeAny[]>;

  if (epochSize === -1) {
    sets[epochSize] = [...data.data];
  } else {
    sets[BASE_EPOCH_SIZE] = [...data.data];
    const baseCount = data.data.length;
    for (const { inSeconds } of EPOCH_SIZES) {
      if (inSeconds === BASE_EPOCH_SIZE) continue;
      sets[inSeconds] = buildDerivedEpochSet(baseCount, inSeconds);
    }
  }
  cache[key] = { ...existing, config: data.config, sets };
  return cache[key];
};

export const fetchEpochSizeData = async (
  rootState: SafeAny,
  id: string,
  epochSize: number,
  notch: string,
  band: string,
  forceRefresh = false
): Promise<SafeAny> => {
  const requestKey = buildRequestKey(id, epochSize, notch, band);
  if (!forceRefresh) {
    const existing = inFlightEpochSizeRequests.get(requestKey);
    if (existing) return existing;
  }

  const url = `${rootState.env?.requestUrl.signals}/studies/${id}/signals/`;
  const request = axiosInstance
    .get(url, {
      params: { epochSize: normalizeEpochSize(epochSize), notch, band }
    })
    .then((response: AxiosResponse<{ data: SafeAny[] }>) => response.data)
    .finally(() => {
      if (!forceRefresh) {
        inFlightEpochSizeRequests.delete(requestKey);
      }
    });

  if (!forceRefresh) {
    inFlightEpochSizeRequests.set(requestKey, request);
  }

  return request;
};

export const storeCurrentEpochData = (
  rootState: SafeAny,
  id: string,
  epochSize: number,
  notch: string,
  band: string,
  data: { data: SafeAny[]; config: SafeAny }
): void => {
  const cached = storeConfigCache(rootState, id, epochSize, notch, band, data);
  rootState.study.config.data = {
    ...cached,
    sets: {
      ...cached.sets
    },
    config: data.config // always refresh config (auth)
  };
};

export const storePreloadedEpochData = (
  rootState: SafeAny,
  id: string,
  epochSize: number,
  notch: string,
  band: string,
  data: { data: SafeAny[]; config: SafeAny }
): void => {
  storeConfigCache(rootState, id, epochSize, notch, band, data);
};

export const preloadAdjacentEpochData = async (
  rootState: SafeAny,
  id: string,
  notch: string,
  band: string,
  currentEpochSize: number
): Promise<void> => {
  if (preloadedStudies.has(id)) return;
  const sortedEpochSizes = EPOCH_SIZES.filter((size) => size.inSeconds !== currentEpochSize).sort(
    (a, b) => Math.abs(a.inSeconds - currentEpochSize) - Math.abs(b.inSeconds - currentEpochSize)
  );

  for (const { inSeconds } of sortedEpochSizes) {
    if (!getCachedEpochConfig(rootState, id, inSeconds, notch, band)) {
      const adjacentEpochData = await fetchEpochSizeData(rootState, id, inSeconds, notch, band);
      storePreloadedEpochData(rootState, id, inSeconds, notch, band, adjacentEpochData);
    }
  }
};

export const preloadAllEpochData = async (
  rootState: SafeAny,
  id: string,
  notch: string,
  band: string
): Promise<void> => {
  const preloadKey = buildPreloadKey(id, notch, band);
  if (preloadedStudies.has(preloadKey)) return;
  preloadedStudies.add(preloadKey);

  const epochSizes = [BASE_EPOCH_SIZE];
  const tasks: Promise<void>[] = [];

  for (const epochSize of epochSizes) {
    if (getCachedEpochConfig(rootState, id, epochSize, notch, band)) continue;
    const task = fetchEpochSizeData(rootState, id, epochSize, notch, band)
      .then((data) => {
        storePreloadedEpochData(rootState, id, epochSize, notch, band, data);
      })
      .catch(() => undefined);
    tasks.push(task);
  }

  await Promise.all(tasks);
};
